/**
 * tutorLiveStudentsViewModel — les élèves de la salle, vus du masteur : la pellicule, les bulles, la mosaïque,
 * la tête du centre (pur).
 *
 * Il reprend ce que chaque tuile de l'ancien live lisait seule dans le store (`LiveParticipant.js`,
 * `LiveParticipantIndicator.js`, `useParticipantState.js`, `NewStudentsListItem.js`) et le rend d'un coup :
 *  - **qui** : les inscrits de la séance (`live_session.users`), sans moi et sans un autre tuteur (l'ancienne
 *    fiche n'avait pas de liste pour un tuteur épinglé, `NewStudentsListItem.js:752`) ;
 *  - **où il est** : là (`liveParticipants`, que l'écouteur global `LKEvents` tient), parti (il est déjà
 *    entré aujourd'hui : une ligne `joined_call` au journal du jour), sinon attendu ;
 *  - **son mode** : Dialogue s'il est dans `soundModeIds`, toujours en séance individuelle
 *    (`useIsSoundMode`) ;
 *  - **une puce, la plus urgente**, dans l'ordre de l'ancienne pastille (`LiveParticipantIndicator.js:49-164`) :
 *    la main levée, hors de l'appli (après 3 s, jamais pendant une photo), avec le champion, le silence
 *    (10 min sans échange), la photo ; le chrono, le design system l'ajoute après (`tutorStudentFaces`). Pas le rendu
 *    tout juste reçu : il se dit dans la colonne du champion (test du 04/11 après-midi) ;
 *  - **sa connexion faible** : les barres de l'ancienne tuile, quand la salle le dit depuis 15 s
 *    (`poorConnectionQualityIds`, que l'écouteur global `LKEvents` tient) ;
 *  - **le chrono** : en Dialogue le temps d'échange, en Concentré le temps depuis le dernier échange. Les
 *    métriques du serveur (`getMetricsV2`) arrivent avec l'heure de leur calcul ; l'ancien live les avançait
 *    toutes les 5 s (`LiveMainLayout`, `BATCH_UPDATE_TIMERS`), ce view-model les avance de l'heure qu'on lui
 *    donne (`now`) : le compte est le même, sans minuterie qui écrive au store ;
 *  - sa liste d'activités, son drapeau de présence, son rattrapage, son objectif de séance, ses non-lus, son
 *    prochain contrôle, et si sa main levée attend « Oui, vas-y » ou « Non ».
 *
 * Pas un `createSelector` sur le store : ses entrées viennent aussi du moteur (les pistes, la voix) et du
 * crochet (l'heure, l'anti-rebond, « Voir plus »). Le crochet le mémoïse sur ces entrées.
 */
import moment from "moment"

import { type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    toStudentAssessments,
    type ApiStudentAssessment,
} from "../../../adapters/student/Assessments/studentAssessmentsAdapter"
import { toSessionSubjects } from "../../../adapters/tutor/Live/sessionObjectiveAdapter"
import {
    attendanceFlagOf,
    displayNameOf,
    isHolidayRoom,
    isRecovery,
    isTutorUser,
    tutorLiveActivitiesOf,
    type ApiActivityTemplate,
    type ApiLiveStudent,
    type ApiTutorLiveSession,
} from "../../../adapters/tutor/Live/tutorLiveStudentAdapter"
import { type UISoundMode } from "../../../ui/types/studentLive"
import { type UITutorLiveStudent, type UITutorLiveStudentState } from "../../../ui/types/tutorLive"
// Les seuils du fil, que les tuiles partagent : le silence à 10 min (`StudentsList.js:38`), la sortie de
// l'appli dite après 3 s (`LiveParticipantIndicator.js:56-77`).
import { isOutOfApp, quizParticipantIds, SILENCE_THRESHOLD_SECONDS } from "./tutorLiveFeedRules"

type Id = number | string

/** Ce que l'ancien live lisait de la tranche `liveMode` pour ses tuiles. */
export type LiveModeReading = {
    soundModeIds?: Id[] | null
    raisedHands?: Id[] | null
    ongoingCheckpointIds?: Id[] | null
    liveParticipants?: Id[] | null
    activityCameraOnIds?: Id[] | null
    poorConnectionQualityIds?: Id[] | null
    activeTime?: ({ id?: Id | null; time?: number | null } | null)[] | null
    activeTimeCalculatedAt?: string | null
    timesSinceLastCall?: Record<string, number | null> | null
    timesSinceLastCallCalculatedAt?: string | null
    /** `[identité de l'appareil]` → l'activité que cet appareil montre (`null` : aucune). */
    seenStudentActivity?: Record<string, Id | null> | null
    /** `[élève]` → l'activité qu'il a ouverte. */
    currentStudentActivity?: Record<string, Id | null> | null
    todayLiveSessionLogs?: ({ user_id?: Id | null; action?: string | null } | null)[] | null
    recentFinalizedActivities?: Record<string, { activityId?: Id | null } | null> | null
}

/** Un participant de la salle, vu du moteur (`RoomParticipant`) : ce que sa vignette en lit. */
export type RoomPresence = {
    id: string
    isLocal?: boolean
    cameraOn: boolean
    micOn: boolean
    /** Ses attributs LiveKit : le quiz de prérequis en cours (`quizParticipantIds`). */
    attributes?: Record<string, string>
}

export type TutorLiveStudentsInputs = {
    session: ApiTutorLiveSession | null | undefined
    meId: string | null | undefined
    liveMode: LiveModeReading
    participants: readonly RoomPresence[]
    /** Les élèves sortis de l'appli, et depuis quand (ms) : l'anti-rebond se lit contre `now`. */
    outOfAppSince: Record<string, number>
    /** Les élèves qui parlent en ce moment (le moteur, `ActiveSpeakersChanged`). */
    speakingIds: readonly string[]
    subjectsById: Record<string, ApiSchoolSubject>
    templates?: ApiActivityTemplate[] | null
    /** Les contrôles à venir par élève (`state.data.schoolAssessments.byStudentId`). */
    assessmentsByStudentId?: Record<string, ApiStudentAssessment[] | null | undefined> | null
    /** Les élèves dont « Voir plus » a déplié les faites. */
    expandedDone: readonly string[]
    /** Ce qui attend une décision du masteur, par élève (le fil). */
    pendingOf?: Record<string, number> | null
    /** Les élèves dont la main levée attend « Oui, vas-y » ou « Non » (le fil). */
    handChoiceOf?: readonly string[] | null
    now: number
}

/** L'id est dans la liste ; la salle écrit ses ids en nombres, les écrans en chaînes : on compare en chaîne. */
export const holdsId = (list: readonly (Id | null)[] | null | undefined, id: Id) =>
    (list ?? []).some((candidate) => candidate != null && String(candidate) === String(id))

/** Les élèves de la séance : les inscrits, sans moi ni un autre tuteur (S-82, F-02). */
export function sessionStudents(
    session: ApiTutorLiveSession | null | undefined,
    meId: string | null | undefined
): ApiLiveStudent[] {
    return (session?.users ?? []).filter(
        (user): user is ApiLiveStudent =>
            user != null && user.id != null && String(user.id) !== String(meId) && !isTutorUser(user)
    )
}

/** La séance est individuelle : le son reste ouvert (`LiveParticipant.js:92`). */
export function isIndividualSession(session: ApiTutorLiveSession | null | undefined): boolean {
    return session?.live_room?.type === "individual"
}

/** Son mode, tel que l'ancienne tuile le lisait (`useIsSoundMode`) : toujours Dialogue en séance individuelle. */
export function soundModeOf(
    session: ApiTutorLiveSession | null | undefined,
    liveMode: LiveModeReading,
    studentId: Id
): UISoundMode {
    if (isIndividualSession(session)) return "dialogue"
    return holdsId(liveMode.soundModeIds, studentId) ? "dialogue" : "focused"
}

/**
 * Tous les inscrits sont en Concentré (l'ancien `areAllStudentsInSilentMode`, `LiveParticipant.js:74-84`) :
 * les élèves de la séance, et aucun dans `soundModeIds`.
 */
export function allStudentsFocused(session: ApiTutorLiveSession | null | undefined, liveMode: LiveModeReading) {
    const students = (session?.users ?? []).filter((user) => user != null && user.is_tutor !== true)
    return students.length > 0 && students.every((student) => !holdsId(liveMode.soundModeIds, student!.id!))
}

/** Un chrono : ses secondes au relevé, et, s'il court, l'instant (ms) d'où il compte. */
type Counted = { seconds: number; runningSince?: number }

/**
 * Le relevé (`base`, calculé à `calculatedAt`) avancé jusqu'à `now`, s'il court. L'ancre (`runningSince`) ne
 * dépend que du relevé : elle ne bouge qu'avec un nouveau relevé, et la puce qui compte à partir d'elle ne réarme
 * rien entre deux. Une bascule de mode, que la salle apprend après le serveur, peut faire surestimer de quelques
 * secondes jusqu'au relevé suivant : c'est la puce qui ne recule pas (`tutorStudentChronoHeld`).
 */
function counted(base: number, running: boolean, calculatedAt: string | null | undefined, now: number): Counted {
    if (!running) return { seconds: base }
    const parsed = calculatedAt ? new Date(calculatedAt).getTime() : NaN
    const at = Number.isNaN(parsed) ? now : parsed
    return { seconds: base + Math.max(0, Math.floor((now - at) / 1000)), runningSince: at - base * 1000 }
}

/** Le temps d'échange (s) : celui du serveur, qui court tant qu'il est là et en Dialogue. */
export function activeSeconds(liveMode: LiveModeReading, studentId: Id, here: boolean, dialogue: boolean, now: number) {
    const base = (liveMode.activeTime ?? []).find((row) => row?.id != null && String(row.id) === String(studentId))
    return counted(base?.time ?? 0, here && dialogue && base != null, liveMode.activeTimeCalculatedAt, now)
}

/** Le temps depuis le dernier échange (s), qui court tant qu'il est là en Concentré (`processMetricsData`). */
export function sinceSeconds(liveMode: LiveModeReading, studentId: Id, here: boolean, dialogue: boolean, now: number) {
    const raw = liveMode.timesSinceLastCall?.[String(studentId)]
    if (raw == null || (here && dialogue)) return { seconds: 0 }
    return counted(raw, here, liveMode.timesSinceLastCallCalculatedAt, now)
}

/**
 * Le chrono de sa vidéo : en Dialogue le temps d'échange, en Concentré le temps depuis le dernier échange. Il court
 * quand le serveur le compte et qu'il est là : `runningSince` dit d'où, pour que la puce avance seule, à la seconde
 * (weekly du 03/11, W9). `since` : le temps depuis le dernier échange, déjà lu pour l'état de la carte.
 */
export function chronoOf(
    liveMode: LiveModeReading,
    studentId: Id,
    here: boolean,
    soundMode: UISoundMode,
    since: Counted,
    now: number
): UITutorLiveStudent["chrono"] {
    if (soundMode !== "dialogue") return { kind: "since", ...since }
    return { kind: "exchange", ...activeSeconds(liveMode, studentId, here, true, now) }
}

/** Sa présence : là, parti (déjà entré aujourd'hui), ou attendu (S-96). */
export function presenceOf(liveMode: LiveModeReading, studentId: Id): UITutorLiveStudent["presence"] {
    if (holdsId(liveMode.liveParticipants, studentId)) return "here"
    const joined = (liveMode.todayLiveSessionLogs ?? []).some(
        (log) => log?.action === "joined_call" && log.user_id != null && String(log.user_id) === String(studentId)
    )
    return joined ? "offline" : "waiting"
}

/**
 * L'activité qu'il a devant lui, sur l'un de ses appareils (port de `useSeenStudentActivity`) : avant que la
 * salle ne le dise, celle qu'il a ouverte. Rien s'il n'est pas là (`NewStudentsListItem.js:324-329`,
 * `TodoModal.js:370-371`). `0` n'est aucune activité : l'écouteur écrit `Number(activity_id)`
 * (`LKEvents.tsx:513-516`, `:673-677`), et l'ancien `0` ne tombait sur aucune ligne. Une seule lecture pour
 * les tuiles et le centre (`useTutorLiveCenter`).
 */
export function seenActivityOf(
    liveMode: {
        seenStudentActivity?: Record<string, unknown> | null
        currentStudentActivity?: Record<string, unknown> | null
    },
    studentId: Id,
    here: boolean
): string | null {
    if (!here) return null
    const devices = Object.entries(liveMode.seenStudentActivity ?? {}).filter(
        ([identity]) => parseInt(identity, 10) === Number(studentId)
    )
    const seen =
        devices.length === 0
            ? liveMode.currentStudentActivity?.[String(studentId)]
            : devices.find(([, activityId]) => activityId != null)?.[1]
    return seen == null || String(seen) === "0" ? null : String(seen)
}

/**
 * La puce d'état, une seule, dans l'ordre de l'ancienne pastille (`LiveParticipantIndicator.js:94-139`) : la
 * main, hors de l'appli (confirmé, et pas pendant une photo), le champion, le silence, la photo. Rien pour un élève
 * absent : sa présence parle.
 */
export function stateOf(
    inputs: Pick<TutorLiveStudentsInputs, "liveMode" | "outOfAppSince" | "now"> & {
        /** Les élèves qui font leur quiz de prérequis : jamais « avec le champion » (`quizParticipantIds`). */
        quizIds?: readonly string[]
    },
    studentId: Id,
    here: boolean,
    mode: UISoundMode,
    since: number
): { state?: UITutorLiveStudentState; stateLabel?: string } {
    if (!here) return {}
    const { liveMode } = inputs
    const camera = holdsId(liveMode.activityCameraOnIds, studentId)
    if (holdsId(liveMode.raisedHands, studentId)) return { state: "hand_raised" }
    if (isOutOfApp(inputs.outOfAppSince[String(studentId)], inputs.now, camera)) return { state: "out_of_app" }
    if (holdsId(liveMode.ongoingCheckpointIds, studentId))
        return { state: holdsId(inputs.quizIds, studentId) ? "quiz" : "with_sam" }
    if (mode === "focused" && since >= SILENCE_THRESHOLD_SECONDS) {
        return { state: "silence", stateLabel: `Silence · ${Math.floor(since / 60)} min` }
    }
    if (camera) return { state: "photo" }
    return {}
}

/** Les élèves de la salle, prêts pour la pellicule. */
export function tutorLiveStudentsViewModel(inputs: TutorLiveStudentsInputs): UITutorLiveStudent[] {
    const { session, liveMode, now } = inputs
    const today = moment(now)
    const holiday = isHolidayRoom(session?.live_room)
    const stateInputs = { ...inputs, quizIds: quizParticipantIds(inputs.participants) }
    return sessionStudents(session, inputs.meId).map((student): UITutorLiveStudent => {
        const id = String(student.id)
        const presence = presenceOf(liveMode, id)
        const here = presence === "here"
        const soundMode = soundModeOf(session, liveMode, id)
        const since = sinceSeconds(liveMode, id, here, soundMode === "dialogue", now)
        const chrono = chronoOf(liveMode, id, here, soundMode, since, now)
        const participant = here
            ? inputs.participants.find((candidate) => candidate.id === id && !candidate.isLocal)
            : undefined
        const { activities, moreDone } = tutorLiveActivitiesOf(student, {
            subjectsById: inputs.subjectsById,
            templates: inputs.templates,
            seenActivityId: seenActivityOf(liveMode, id, here),
            expanded: inputs.expandedDone.includes(id),
            today,
        })
        const assessments = inputs.assessmentsByStudentId?.[id]
        const unread = session?.unread_private_messages_count?.[id] ?? 0
        return {
            id,
            firstname: displayNameOf(student),
            grade: student.profile?.class ?? undefined,
            avatarUrl: student.avatar ?? undefined,
            soundMode,
            presence,
            recovery: isRecovery(session, id) || undefined,
            flag: attendanceFlagOf(session, student, today),
            ...stateOf(stateInputs, id, here, soundMode, since.seconds),
            chrono,
            activities,
            moreDone: moreDone || undefined,
            speaking: here && inputs.speakingIds.includes(id) ? true : undefined,
            cameraOff: participant != null ? !participant.cameraOn : undefined,
            micOff: participant != null ? !participant.micOn : undefined,
            poorConnection: here && holdsId(liveMode.poorConnectionQualityIds, id) ? true : undefined,
            unread: unread > 0 ? unread : undefined,
            pending: inputs.pendingOf?.[id] || undefined,
            handChoice: inputs.handChoiceOf?.includes(id) || undefined,
            // La ligne d'objectif, sur tous les lives (round 4) : ses matières ; ce qu'il vise n'est pas servi ici.
            objective: { subjects: toSessionSubjects(student, session?.id, inputs.subjectsById) },
            // Hors stage : le prochain contrôle, une fois lu (avant : pas de widget, plutôt qu'un « Ajouter » faux).
            nextAssessment:
                holiday || assessments === undefined ? undefined : (toStudentAssessments(assessments ?? [])[0] ?? null),
        }
    })
}

/**
 * L'élève regardé par défaut (S-80) : sous `events-panel` (la seule salle que le design system ouvre), le
 * premier élève inscrit (`TutorLiveScreen.js:260-267`).
 */
export function defaultSelectedId(students: readonly UITutorLiveStudent[]): string | undefined {
    return students[0]?.id
}

/**
 * L'élève dont la bascule part : `setSoundModeV2` en cours le concerne (`LiveParticipant.js:250-255`, la tranche
 * porte le corps de la requête pendant qu'elle part).
 */
export function modePendingIdOf(
    slice: { request?: boolean; data?: { user_ids?: Id[] | null } | null } | null | undefined
): string | undefined {
    if (slice?.request !== true) return undefined
    const id = slice.data?.user_ids?.[0]
    return id != null ? String(id) : undefined
}
